import { FormControl, FormHelperText, FormLabel, SxProps, Theme } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type RHFMultiCheckboxProps<T extends string | number> = {
  name: string;
  options: { label: string; value: T }[];
  row?: boolean;
  label?: string;
  spacing?: number;
  helperText?: ReactNode;
  onChange: (values: T[]) => void;
  sx: SxProps<Theme>;
  disabled?: boolean;
};

/**
 * Minimal@4 component.
 */
export default function RHFMultiCheckbox<T extends string | number>({
  row,
  name,
  label,
  options,
  spacing,
  helperText,
  onChange,
  disabled = false,
  ...other
}: RHFMultiCheckboxProps<T>) {
  const { control } = useFormContext();

  const getSelected = (selectedItems: T[], item: T) =>
    selectedItems.includes(item) ? selectedItems.filter((value) => value !== item) : [...selectedItems, item];

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <FormControl component="fieldset">
          {label ? (
            <FormLabel component="legend" sx={{ typography: 'body2' }}>
              {label}
            </FormLabel>
          ) : null}

          <FormGroup
            sx={{
              ...(row && {
                flexDirection: 'row',
              }),
              '& .MuiFormControlLabel-root': {
                '&:not(:last-of-type)': {
                  mb: spacing || 0,
                },
                ...(row && {
                  mr: 0,
                  '&:not(:last-of-type)': {
                    mr: spacing || 2,
                  },
                }),
              },
            }}
          >
            {options.map((option, index) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={field.value?.includes(option.value)}
                    onChange={() => onChange(getSelected(field.value, option.value))}
                    disabled={disabled}
                    inputRef={index === 0 ? ref : undefined}
                  />
                }
                label={option.label}
                {...other}
              />
            ))}
          </FormGroup>

          {!!error || helperText ? (
            <FormHelperText error={!!error} sx={{ mx: 0 }}>
              {error ? error?.message : helperText}
            </FormHelperText>
          ) : null}
        </FormControl>
      )}
    />
  );
}
